import React, { useState, useEffect, useCallback } from 'react'
import { apiGet, apiPost } from '../../utils/api'

// ─── Types ──────────────────────────────────────────────────────────
interface KBStats {
  total_articles: number
  total_words: number
  categories: number
  total_syncs: number
  last_sync: KBSyncRun | null
  last_changes: number
}

interface KBArticle {
  id: number
  slug: string
  title: string
  category: string
  word_count: number
  source_url: string
  body_html?: string
  body_text?: string
  sort_order: number
  first_scraped_at: string
  last_scraped_at: string
  last_changed_at: string | null
  is_deleted: number
}

interface KBCategory {
  category: string
  count: number
}

interface KBSyncRun {
  id: number
  started_at: string
  finished_at: string | null
  status: string
  triggered_by: string
  source_url: string
  total_articles: number
  articles_added: number
  articles_changed: number
  articles_removed: number
  articles_unchanged: number
  error_message: string | null
}

interface KBChangeLogEntry {
  id: number
  sync_id: number
  article_id: number
  change_type: string
  field_changed: string | null
  old_value: string | null
  new_value: string | null
  diff_summary: string | null
  created_at: string
  article_title?: string
  article_slug?: string
}

type TabId = 'mirror' | 'articles' | 'changelog' | 'syncs'

// ─── Main View ──────────────────────────────────────────────────────
export const KBMirrorView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('articles')
  const [stats, setStats] = useState<KBStats | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiGet<KBStats>('/kb-mirror/stats')
      setStats(data)
    } catch (err) {
      console.error('Failed to fetch KB stats:', err)
    }
  }, [])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const handleSync = async () => {
    setIsSyncing(true)
    setSyncMessage(null)
    try {
      const result = await apiPost<{ message: string; sync_id: number }>('/kb-mirror/sync')
      setSyncMessage(`Sync started (ID: ${result.sync_id}). Scraping articles in the background...`)
      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const runData = await apiGet<{ sync: KBSyncRun; changes: KBChangeLogEntry[] }>(`/kb-mirror/syncs/${result.sync_id}`)
          const run = runData.sync
          if (run.status === 'completed') {
            clearInterval(pollInterval)
            setIsSyncing(false)
            setSyncMessage(`Sync complete: ${run.articles_added ?? 0} added, ${run.articles_changed ?? 0} changed, ${run.articles_removed ?? 0} removed, ${run.articles_unchanged ?? 0} unchanged`)
            fetchStats()
          } else if (run.status === 'failed') {
            clearInterval(pollInterval)
            setIsSyncing(false)
            setSyncMessage(`Sync failed: ${run.error_message || 'Unknown error'}`)
          }
        } catch {
          // Keep polling
        }
      }, 2000)
      // Safety timeout after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval)
        if (isSyncing) {
          setIsSyncing(false)
          setSyncMessage('Sync is taking longer than expected. Check Sync History for status.')
        }
      }, 300000)
    } catch (err) {
      setIsSyncing(false)
      setSyncMessage(`Sync failed: ${(err as Error).message}`)
    }
  }

  const tabs: { id: TabId; label: string }[] = [
    { id: 'articles', label: 'Articles' },
    { id: 'mirror', label: 'URL Mirror' },
    { id: 'changelog', label: 'Change Log' },
    { id: 'syncs', label: 'Sync History' },
  ]

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
              Knowledge Base Mirror
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {stats ? (
                <>{stats.total_articles} articles across {stats.categories} categories &middot; {stats.total_words.toLocaleString()} words
                  {stats.last_sync?.finished_at && <> &middot; Last sync: {new Date(stats.last_sync.finished_at).toLocaleString()}</>}
                </>
              ) : 'Loading...'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {syncMessage && (
              <span className={`text-sm ${isSyncing ? 'text-blue-600' : syncMessage.includes('failed') ? 'text-red-600' : 'text-green-600'}`}>
                {syncMessage}
              </span>
            )}
            <button
              onClick={handleSync}
              disabled={isSyncing}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSyncing ? (
                <>
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Syncing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M2.985 19.644l3.182-3.182" />
                  </svg>
                  Sync Now
                </>
              )}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4 -mb-px">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'articles' && <ArticlesTab />}
        {activeTab === 'mirror' && <MirrorTab />}
        {activeTab === 'changelog' && <ChangeLogTab />}
        {activeTab === 'syncs' && <SyncHistoryTab onSyncComplete={fetchStats} />}
      </div>
    </div>
  )
}

// ─── Articles Tab ───────────────────────────────────────────────────
const ArticlesTab: React.FC = () => {
  const [articles, setArticles] = useState<KBArticle[]>([])
  const [categories, setCategories] = useState<KBCategory[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedArticle, setSelectedArticle] = useState<KBArticle | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchArticles = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string> = {}
      if (selectedCategory !== 'all') params.category = selectedCategory
      if (searchTerm) params.search = searchTerm
      const qs = new URLSearchParams(params).toString()
      const data = await apiGet<{ articles: KBArticle[] }>(`/kb-mirror/articles${qs ? `?${qs}` : ''}`)
      setArticles(data.articles || [])
    } catch (err) {
      console.error('Failed to fetch articles:', err)
    } finally {
      setLoading(false)
    }
  }, [selectedCategory, searchTerm])

  const fetchCategories = useCallback(async () => {
    try {
      const data = await apiGet<{ categories: KBCategory[] }>('/kb-mirror/categories')
      setCategories(data.categories || [])
    } catch (err) {
      console.error('Failed to fetch categories:', err)
    }
  }, [])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  useEffect(() => {
    const timer = setTimeout(() => fetchArticles(), searchTerm ? 300 : 0)
    return () => clearTimeout(timer)
  }, [fetchArticles, searchTerm])

  const handleArticleClick = async (article: KBArticle) => {
    try {
      const data = await apiGet<{ article: KBArticle }>(`/kb-mirror/articles/${article.slug}`)
      setSelectedArticle(data.article)
    } catch (err) {
      console.error('Failed to fetch article:', err)
    }
  }

  if (selectedArticle) {
    return <ArticleDetail article={selectedArticle} onBack={() => setSelectedArticle(null)} />
  }

  return (
    <div className="flex h-full">
      {/* Category Sidebar */}
      <div className="w-56 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 overflow-y-auto bg-gray-50 dark:bg-gray-800/50">
        <div className="p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 px-2 mb-2">Categories</p>
          <button
            onClick={() => setSelectedCategory('all')}
            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
              selectedCategory === 'all'
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 font-medium'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            All Articles
            <span className="ml-auto float-right text-xs text-gray-400">{articles.length}</span>
          </button>
          {categories.map(cat => (
            <button
              key={cat.category}
              onClick={() => setSelectedCategory(cat.category)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                selectedCategory === cat.category
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 font-medium'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {cat.category}
              <span className="ml-auto float-right text-xs text-gray-400">{cat.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Article List */}
      <div className="flex-1 overflow-y-auto">
        {/* Search */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-3 z-10">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search articles..."
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-400">
            <svg className="w-5 h-5 animate-spin mr-2" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Loading articles...
          </div>
        ) : articles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
            <p className="text-sm font-medium">No articles found</p>
            <p className="text-xs mt-1">Run a sync to scrape the knowledge base</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {articles.map(article => (
              <button
                key={article.id}
                onClick={() => handleArticleClick(article)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{article.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                        {article.category}
                      </span>
                      <span className="text-[11px] text-gray-400">{article.word_count} words</span>
                      {article.is_deleted ? (
                        <span className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-600">Removed</span>
                      ) : null}
                    </div>
                  </div>
                  <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Article Detail ─────────────────────────────────────────────────
const ArticleDetail: React.FC<{ article: KBArticle; onBack: () => void }> = ({ article, onBack }) => {
  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Back button */}
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-4 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Back to Articles
        </button>

        {/* Meta */}
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
            {article.category}
          </span>
          <span className="text-xs text-gray-400">{article.word_count} words</span>
          <a
            href={article.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-500 hover:text-blue-600 hover:underline ml-auto"
          >
            View Original &rarr;
          </a>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{article.title}</h2>
        <p className="text-xs text-gray-400 mb-6">
          Last scraped: {new Date(article.last_scraped_at).toLocaleString()}
          {article.last_changed_at && <> &middot; Last changed: {new Date(article.last_changed_at).toLocaleString()}</>}
        </p>

        {/* Body */}
        <div
          className="prose prose-sm dark:prose-invert max-w-none
            prose-headings:text-gray-900 dark:prose-headings:text-white
            prose-p:text-gray-700 dark:prose-p:text-gray-300
            prose-a:text-blue-600 prose-strong:text-gray-900 dark:prose-strong:text-white
            prose-ul:text-gray-700 dark:prose-ul:text-gray-300
            prose-ol:text-gray-700 dark:prose-ol:text-gray-300
            prose-li:text-gray-700 dark:prose-li:text-gray-300
            prose-blockquote:border-blue-300 prose-blockquote:text-gray-600 dark:prose-blockquote:text-gray-400"
          dangerouslySetInnerHTML={{ __html: article.body_html || '<p class="text-gray-400 italic">No content available. Run a sync to populate.</p>' }}
        />
      </div>
    </div>
  )
}

// ─── URL Mirror Tab ─────────────────────────────────────────────────
const MirrorTab: React.FC = () => {
  return (
    <div className="h-full flex flex-col">
      <div className="flex-shrink-0 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center gap-2">
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.07-9.07l-1.757 1.757a4.5 4.5 0 01-6.364 6.364l4.5-4.5a4.5 4.5 0 017.244 1.242z" />
        </svg>
        <span className="text-xs text-gray-500 dark:text-gray-400">Live mirror of</span>
        <a
          href="https://v1qw2026bk.framer.website/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-blue-500 hover:text-blue-600 hover:underline"
        >
          https://v1qw2026bk.framer.website/
        </a>
        <a
          href="https://v1qw2026bk.framer.website/"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
        >
          Open in new tab
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
          </svg>
        </a>
      </div>
      <iframe
        src="https://v1qw2026bk.framer.website/"
        className="flex-1 w-full border-0"
        title="WeQuote Knowledge Base"
        sandbox="allow-scripts allow-same-origin allow-popups"
      />
    </div>
  )
}

// ─── Change Log Tab ─────────────────────────────────────────────────
const ChangeLogTab: React.FC = () => {
  const [entries, setEntries] = useState<KBChangeLogEntry[]>([])
  const [filterType, setFilterType] = useState<string>('all')
  const [loading, setLoading] = useState(true)

  const fetchChanges = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string> = {}
      if (filterType !== 'all') params.change_type = filterType
      const qs = new URLSearchParams(params).toString()
      const data = await apiGet<{ changelog: KBChangeLogEntry[] }>(`/kb-mirror/changelog${qs ? `?${qs}` : ''}`)
      setEntries(data.changelog || [])
    } catch (err) {
      console.error('Failed to fetch changelog:', err)
    } finally {
      setLoading(false)
    }
  }, [filterType])

  useEffect(() => {
    fetchChanges()
  }, [fetchChanges])

  const changeTypeColors: Record<string, string> = {
    added: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    modified: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    removed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    restored: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  }

  const changeTypeIcons: Record<string, string> = {
    added: '+',
    modified: '~',
    removed: '-',
    restored: '↺',
  }

  return (
    <div className="h-full overflow-y-auto">
      {/* Filter bar */}
      <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-3 z-10 flex items-center gap-2">
        <span className="text-xs text-gray-500 dark:text-gray-400">Filter:</span>
        {['all', 'added', 'modified', 'removed', 'restored'].map(type => (
          <button
            key={type}
            onClick={() => setFilterType(type)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              filterType === type
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <svg className="w-5 h-5 animate-spin mr-2" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading change log...
        </div>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          <p className="text-sm font-medium">No changes recorded yet</p>
          <p className="text-xs mt-1">Changes will appear after running multiple syncs</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {entries.map(entry => (
            <div key={entry.id} className="px-4 py-3 flex items-start gap-3">
              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold flex-shrink-0 mt-0.5 ${changeTypeColors[entry.change_type] || 'bg-gray-100 text-gray-500'}`}>
                {changeTypeIcons[entry.change_type] || '?'}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${changeTypeColors[entry.change_type] || 'bg-gray-100 text-gray-500'}`}>
                    {entry.change_type}
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {entry.article_title || `Article #${entry.article_id}`}
                  </span>
                </div>
                {entry.diff_summary && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{entry.diff_summary}</p>
                )}
                {entry.field_changed && entry.field_changed !== 'body' && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    Field: {entry.field_changed}
                    {entry.old_value && <> — was: <span className="line-through text-red-400">{entry.old_value}</span></>}
                    {entry.new_value && <> — now: <span className="text-green-500">{entry.new_value}</span></>}
                  </p>
                )}
                <p className="text-[11px] text-gray-400 mt-1">
                  Sync #{entry.sync_id} &middot; {new Date(entry.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Sync History Tab ───────────────────────────────────────────────
const SyncHistoryTab: React.FC<{ onSyncComplete?: () => void }> = () => {
  const [syncs, setSyncs] = useState<KBSyncRun[]>([])
  const [selectedSync, setSelectedSync] = useState<(KBSyncRun & { changes?: KBChangeLogEntry[] }) | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchSyncs = useCallback(async () => {
    setLoading(true)
    try {
      const data = await apiGet<{ syncs: KBSyncRun[] }>('/kb-mirror/syncs')
      setSyncs(data.syncs || [])
    } catch (err) {
      console.error('Failed to fetch syncs:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSyncs()
  }, [fetchSyncs])

  const handleSyncClick = async (sync: KBSyncRun) => {
    try {
      const data = await apiGet<{ sync: KBSyncRun; changes: KBChangeLogEntry[] }>(`/kb-mirror/syncs/${sync.id}`)
      setSelectedSync({ ...data.sync, changes: data.changes || [] })
    } catch (err) {
      console.error('Failed to fetch sync details:', err)
    }
  }

  const statusColors: Record<string, string> = {
    completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    running: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  }

  return (
    <div className="h-full overflow-y-auto">
      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <svg className="w-5 h-5 animate-spin mr-2" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading sync history...
        </div>
      ) : syncs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <svg className="w-12 h-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M2.985 19.644l3.182-3.182" />
          </svg>
          <p className="text-sm font-medium">No syncs yet</p>
          <p className="text-xs mt-1">Click "Sync Now" to scrape the knowledge base</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {syncs.map(sync => (
            <button
              key={sync.id}
              onClick={() => handleSyncClick(sync)}
              className={`w-full text-left px-4 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${
                selectedSync?.id === sync.id ? 'bg-blue-50 dark:bg-blue-900/10' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColors[sync.status] || 'bg-gray-100 text-gray-500'}`}>
                    {sync.status}
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Sync #{sync.id}
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(sync.started_at).toLocaleString()}
                  </span>
                </div>
                {sync.status === 'running' && (
                  <svg className="w-4 h-4 animate-spin text-blue-500" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
              </div>
              {sync.status === 'completed' && (
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>{sync.total_articles} total</span>
                  {sync.articles_added > 0 && <span className="text-green-600">+{sync.articles_added} added</span>}
                  {sync.articles_changed > 0 && <span className="text-yellow-600">~{sync.articles_changed} changed</span>}
                  {sync.articles_removed > 0 && <span className="text-red-600">-{sync.articles_removed} removed</span>}
                  <span className="text-gray-400">{sync.articles_unchanged} unchanged</span>
                  {sync.finished_at && (
                    <span className="ml-auto text-gray-400">
                      Duration: {Math.round((new Date(sync.finished_at).getTime() - new Date(sync.started_at).getTime()) / 1000)}s
                    </span>
                  )}
                </div>
              )}
              {sync.status === 'failed' && sync.error_message && (
                <p className="text-xs text-red-500 mt-1 truncate">{sync.error_message}</p>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Sync Detail Panel */}
      {selectedSync && selectedSync.changes && selectedSync.changes.length > 0 && (
        <div className="border-t-2 border-blue-200 dark:border-blue-800 bg-gray-50 dark:bg-gray-800/50">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              Changes in Sync #{selectedSync.id}
            </h3>
            <button
              onClick={() => setSelectedSync(null)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Close
            </button>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {selectedSync.changes.map((change: KBChangeLogEntry, idx: number) => (
              <div key={idx} className="px-4 py-2 text-xs">
                <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium mr-2 ${
                  change.change_type === 'added' ? 'bg-green-100 text-green-700' :
                  change.change_type === 'modified' ? 'bg-yellow-100 text-yellow-700' :
                  change.change_type === 'removed' ? 'bg-red-100 text-red-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {change.change_type}
                </span>
                <span className="text-gray-700 dark:text-gray-300">{change.article_title || `Article #${change.article_id}`}</span>
                {change.diff_summary && <span className="text-gray-400 ml-2">— {change.diff_summary}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
